import React, { useState } from "react";
import { toast } from "sonner";

import { useCreateInterface, useDownloadTeamsApp, useSaveCredentials } from "@/shared/api/interfaces";
import { LinkField } from "@/shared/components/LinkField";
import { FieldLabel, GhostButton, PrimaryButton, TextInput } from "@/shared/components/settings-atoms";
import { catalogFor } from "@/shared/lib/interface-catalog";
import { FormError } from "./FormError";
import { InterfaceDialog } from "./InterfaceDialog";
import { LinkedAppsWarning } from "./LinkedAppsWarning";
import { Steps } from "./Steps";

import type { Interface } from "@/shared/types";
import type { SetupProps } from "../types";

const azure = (
  <a
    href="https://portal.azure.com"
    target="_blank"
    rel="noopener noreferrer"
    className="font-medium text-[var(--text-primary)] underline underline-offset-2"
  >
    Azure portal
  </a>
);

const NEW_BOT = [
  <>Open the {azure}, choose Create a resource, then Azure Bot. Set the type to Single Tenant.</>,
  <>Copy the App ID and the Tenant ID from the bot, and paste them below.</>,
  <>Open Configuration → Manage, create a new client secret, copy its value and paste it below.</>,
];

const NEW_SECRET = [
  <>Open your bot in the {azure}, then Configuration → Manage.</>,
  <>Create a new client secret and copy its value.</>,
  <>Paste it below with the App ID and Tenant ID.</>,
];

const NOTE = "text-[12.5px] leading-[1.5] text-[var(--text-tertiary)]";

/** Microsoft's answers, in this dialog's words. */
const WORDS = {
  3110001: "Microsoft rejected those keys. Check the App ID, client secret and tenant ID.",
  3110002: "That bot is already connected to another agent.",
  3110003: "Couldn't reach Microsoft. Try again.",
};

const FIELDS = ["app_id", "tenant_id", "app_password"] as const;

/**
 * Connect a Teams bot by the keys of its own Azure Bot, or give a broken one a new secret.
 *
 * The secret is read from the field when it is sent
 * and never held in React state, and a successful connect does not close the
 * dialog: the bot is useless until Azure points at our endpoint and the app is
 * in Teams, so it shows the endpoint and the app download, and Done closes it.
 */
export function TeamsSetup({ agent, existing, onClose }: SetupProps) {
  const create = useCreateInterface();
  const save = useSaveCredentials();
  const download = useDownloadTeamsApp();
  const write = existing ? save : create;
  const [filled, setFilled] = useState<Record<string, boolean>>({});
  const [connected, setConnected] = useState<Interface | null>(null);
  const meta = catalogFor("teams")!;

  const connect = async (values: Record<string, string>) => {
    const credentials = {
      app_id: (values.app_id ?? "").trim(),
      app_password: (values.app_password ?? "").trim(),
      tenant_id: (values.tenant_id ?? "").trim(),
    };
    try {
      const done = existing
        ? await save.send({ id: existing.id, credentials })
        : await create.send({ agent_id: agent.id, type: "teams", credentials });
      toast.success(`${done.label ?? meta.name} is connected`);
      setConnected(done);
    } catch {
      // FormError shows it under the fields, from the mutation's own state.
    }
  };

  if (connected) {
    const endpoint = connected.setup.messaging_endpoint;
    return (
      <InterfaceDialog
        meta={meta}
        title={`${connected.label ?? meta.name} is ${existing ? "reconnected" : "connected"}`}
        description={`Two more steps so people can message ${agent.name} in Teams: point Azure at it, then add the app.`}
        onClose={onClose}
        footer={<PrimaryButton type="button" onClick={onClose}>Done</PrimaryButton>}
      >
        <Steps
          items={[
            <>
              <p className="m-0 mb-1.5">Paste this in Azure Bot → Configuration → Messaging endpoint, and save.</p>
              {endpoint && (
                <>
                  <FieldLabel>Messaging endpoint</FieldLabel>
                  <LinkField url={endpoint} label="messaging endpoint" />
                </>
              )}
            </>,
            <>In Azure Bot → Channels → Microsoft Teams → Apply.</>,
            <>
              <p className="m-0 mb-1.5">Download the app for Teams.</p>
              <PrimaryButton type="button" onClick={() => download.mutate(connected.id)} disabled={download.isPending}>
                {download.isPending ? "Downloading…" : "Download Teams app"}
              </PrimaryButton>
              <FormError error={download.error} />
            </>,
            <>
              In Teams, open Apps → Manage your apps → Upload an app → Upload a custom app and choose the zip. Or a
              Teams admin can add it for everyone in Teams admin center → Manage apps → Upload new app.
            </>,
          ]}
        />
        <p className={`m-0 ${NOTE}`}>
          Your organization must allow custom app upload: "Upload custom apps" in the Teams app setup policy. It
          answers 1:1 chats and @mentions at once. Pick channels it should answer in full from the interface's menu,
          under Bot channels.
        </p>
      </InterfaceDialog>
    );
  }

  return (
    <InterfaceDialog
      meta={meta}
      title={existing ? `Reconnect ${existing.label ?? meta.name}` : "Connect Microsoft Teams"}
      description={existing?.error ?? meta.desc}
      onSubmit={connect}
      onClose={onClose}
      footer={
        <>
          <GhostButton type="button" onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton type="submit" disabled={!FIELDS.every((name) => filled[name]) || write.isPending}>
            {write.isPending ? "Connecting…" : "Connect"}
          </PrimaryButton>
        </>
      }
    >
      <Steps items={existing ? NEW_SECRET : NEW_BOT} />
      {(
        [
          ["app_id", "App ID", "text"],
          ["tenant_id", "Tenant ID", "text"],
          ["app_password", "Client secret", "password"],
        ] as const
      ).map(([name, label, type]) => (
        <div key={name}>
          <FieldLabel>{label}</FieldLabel>
          <TextInput
            name={name}
            aria-label={label}
            type={type}
            autoComplete="off"
            spellCheck={false}
            placeholder={type === "password" ? "Value, not Secret ID" : "00000000-0000-0000-0000-000000000000"}
            onChange={(value) => {
              setFilled((was) => ({ ...was, [name]: !!value.trim() }));
              if (write.error) write.reset();
            }}
          />
        </div>
      ))}
      <FormError error={write.error} words={WORDS} />
      {!existing && <LinkedAppsWarning agent={agent} />}
    </InterfaceDialog>
  );
}
